import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useDeferredValue, useEffect, useState, type FormEvent } from "react";

import { isApiClientError } from "../api/errors";
import {
  type AccessScope,
  type Department,
  type Membership,
  type Role,
} from "../entities/access";
import { shellBootstrapKey } from "./bootstrap";
import type { ShellServices } from "./services";
import { EmptyState, LoadingPage } from "./states";
import { Select } from "./Select";
import { Notice } from "./Notice";

type Notice = { tone: "success" | "error"; text: string } | null;

/**
 * Цель назначения внутри текущего контура: либо контур целиком, либо одно из
 * его подразделений. Между контурами назначения не делаются — для этого
 * открывается страница нужного контура.
 */
type AssignmentTarget = {
  id: string;
  name: string;
  departmentId: string | null;
};

const WHOLE_SCOPE = "scope";

function normalized(value: string): string {
  return value.trim().toLocaleLowerCase("ru");
}

function subdivisionCount(count: number): string {
  const remainder = count % 100;
  const unit = count % 10;
  const word =
    remainder >= 11 && remainder <= 14
      ? "подразделений"
      : unit === 1
        ? "подразделение"
        : unit >= 2 && unit <= 4
          ? "подразделения"
          : "подразделений";
  return `${count} ${word}`;
}

function descendantIds(
  departments: readonly Department[],
  rootId: string,
): Set<string> {
  const blocked = new Set<string>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const department of departments) {
      if (
        department.parent_id &&
        blocked.has(department.parent_id) &&
        !blocked.has(department.id)
      ) {
        blocked.add(department.id);
        changed = true;
      }
    }
  }
  return blocked;
}

function message(error: unknown): string {
  if (isApiClientError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return "Неизвестная ошибка";
}

function ScopeForm({
  onSave,
  pending,
}: {
  onSave: (value: { code: string; name: string }) => void;
  pending: boolean;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  return (
    <form
      className="scope-inline-form"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onSave({ code: code.trim(), name: name.trim() });
        setCode("");
        setName("");
      }}
    >
      <label>
        <span>Код</span>
        <input
          required
          maxLength={64}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </label>
      <label>
        <span>Название</span>
        <input
          required
          maxLength={255}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <button className="button button--primary" disabled={pending}>
        {pending ? "Создаём…" : "Создать КПО"}
      </button>
    </form>
  );
}

function ScopeList({ services }: { services: ShellServices }) {
  const client = useQueryClient();
  const [notice, setNotice] = useState<Notice>(null);
  // Одно дерево вместо нескольких запросов: тип узла приходит с бэкенда,
  // поэтому код головной организации фронту знать не нужно.
  const tree = useQuery({
    queryKey: ["organization-tree"],
    queryFn: () => services.administration.organizationTree(),
  });
  const createMutation = useMutation({
    mutationFn: (input: { code: string; name: string }) =>
      services.administration.createScope(input),
    onSuccess: async () => {
      setNotice({ tone: "success", text: "КПО создано." });
      await client.invalidateQueries({ queryKey: ["organization-tree"] });
      await client.invalidateQueries({ queryKey: ["scopes"] });
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  if (tree.isPending)
    return <LoadingPage label="Загружаем структуру компании" />;
  if (tree.isError) {
    return (
      <EmptyState
        title="Не удалось загрузить структуру компании"
        description={message(tree.error)}
      />
    );
  }
  // Корень дерева — головная организация, её дети это и подразделения, и КПО.
  const core = tree.data.find((node) => node.type === "core");
  const roots = core ? core.children : tree.data;
  const kpo = roots.filter((node) => node.type === "kpo");
  const coreDepartments = roots.filter((node) => node.type === "department");
  return (
    <>
      {notice ? (
        <p
          className={`operation-notice operation-notice--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}
      <div className="organization-tree">
        {core ? (
          <Link
            className="scope-card scope-card--corporate"
            to="/administration/scopes/$scopeId"
            params={{ scopeId: core.id }}
          >
            <span className="eyebrow">Головная компания</span>
            <strong>{core.name}</strong>
            <span>Открыть состав и подразделения →</span>
          </Link>
        ) : (
          <article className="scope-card scope-card--corporate">
            <span className="eyebrow">Головная компания</span>
            <strong>Алтай Эко Парк</strong>
            <span>Корпоративный уровень управления подразделениями</span>
          </article>
        )}
        <div
          className="organization-tree__children"
          aria-label="Подразделения компании"
        >
          {coreDepartments.map((department) => (
            <Link
              key={department.id}
              className="scope-card"
              to="/administration/scopes/$scopeId/departments/$departmentId"
              params={{
                scopeId: department.scope_id,
                departmentId: department.id,
              }}
            >
              <span className="eyebrow">Подразделение · {department.code}</span>
              <strong>{department.name}</strong>
              <span>Открыть состав и подразделения →</span>
            </Link>
          ))}
        </div>
        {!coreDepartments.length ? (
          <p className="scope-empty">
            В компании пока нет доступных вам подразделений.
          </p>
        ) : null}
      </div>
      <section className="scope-section">
        <div className="panel-heading">
          <div>
            <h2>КПО</h2>
            <p>
              У каждого КПО своя оргструктура, свой состав и свои документы.
            </p>
          </div>
        </div>
        <div className="organization-tree__children" aria-label="КПО">
          {kpo.map((scope) => (
            <Link
              key={scope.id}
              className="scope-card"
              to="/administration/scopes/$scopeId"
              params={{ scopeId: scope.id }}
            >
              <span className="eyebrow">КПО · {scope.code}</span>
              <strong>{scope.name}</strong>
              <span>Открыть состав и подразделения →</span>
            </Link>
          ))}
        </div>
        {!kpo.length ? (
          <p className="scope-empty">Доступных вам КПО пока нет.</p>
        ) : null}
      </section>
      <section className="scope-section">
        <div className="panel-heading">
          <div>
            <h2>Новое КПО</h2>
            <p>Подразделения создаются внутри уже существующего КПО.</p>
          </div>
        </div>
        <ScopeForm
          pending={createMutation.isPending}
          onSave={(input) => createMutation.mutate(input)}
        />
      </section>
    </>
  );
}

function DepartmentForm({
  department,
  departments,
  defaultParentId,
  onSave,
  pending,
}: {
  department: Department | null;
  departments: Department[];
  defaultParentId?: string;
  onSave: (value: {
    code: string;
    name: string;
    parent_id?: string | null;
  }) => void;
  pending: boolean;
}) {
  const [code, setCode] = useState(department?.code ?? "");
  const [name, setName] = useState(department?.name ?? "");
  const [parentId, setParentId] = useState(
    department?.parent_id ?? defaultParentId ?? "",
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const unavailableParents = department
    ? descendantIds(departments, department.id)
    : new Set<string>();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const duplicateDepartment = departments.find(
      (item) =>
        item.id !== department?.id &&
        (normalized(item.code) === normalized(code) ||
          normalized(item.name) === normalized(name)),
    );
    if (duplicateDepartment) {
      setValidationError(
        "Подразделение с таким названием или кодом уже существует в организации.",
      );
      return;
    }
    setValidationError(null);
    onSave({
      code: code.trim(),
      name: name.trim(),
      parent_id: parentId || null,
    });
  };
  const parentOptions = departments.filter(
    (item) => !unavailableParents.has(item.id),
  );
  return (
    <form className="scope-inline-form" onSubmit={submit}>
      <label>
        <span>Код</span>
        <input
          required
          maxLength={64}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </label>
      <label>
        <span>Название</span>
        <input
          required
          maxLength={255}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label>
        <span>Родительское подразделение</span>
        <Select
          value={parentId}
          onChange={(event) => setParentId(event.target.value)}
        >
          <option value="">Нет</option>
          {parentOptions.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </Select>
      </label>
      <button className="button button--primary" disabled={pending}>
        {pending ? "Сохраняем…" : department ? "Сохранить" : "Создать"}
      </button>
      {validationError ? <Notice>{validationError}</Notice> : null}
    </form>
  );
}

/**
 * Подпись человека в списке: имя, а не идентификатор.
 *
 * Ядро отдаёт в составе организации краткую карточку пользователя, поэтому имя
 * есть сразу. Идентификатор остаётся запасным вариантом — на случай записи,
 * созданной до появления карточки.
 */
function personName(membership: Membership): string {
  const user = membership.user;
  const full = [user.last_name, user.first_name].filter(Boolean).join(" ");
  return user.display_name ?? (full || membership.user_id);
}

function MembershipTable({
  memberships,
  departments,
  contextName,
  onRemove,
  pending,
}: {
  memberships: Membership[];
  departments: Department[];
  contextName: string;
  onRemove: (membership: Membership) => void;
  pending: boolean;
}) {
  const departmentsById = new Map(
    departments.map((department) => [department.id, department.name]),
  );
  if (!memberships.length)
    return <p className="scope-empty">В {contextName} пока нет участников.</p>;
  return (
    <div className="scope-table-wrap">
      <table className="scope-table">
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Подразделение</th>
            <th>Должность</th>
            <th>Статус</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {memberships.map((membership) => (
            <tr key={membership.id}>
              <td>
                <a
                  href={`/administration/users/list?user=${membership.user_id}`}
                >
                  {personName(membership)}
                </a>
                {membership.user.email ? (
                  <small className="scope-table__note">
                    {membership.user.email}
                  </small>
                ) : null}
              </td>
              <td>
                {membership.department_id
                  ? (departmentsById.get(membership.department_id) ??
                    "Не назначено")
                  : "Не назначено"}
              </td>
              <td>{membership.position ?? "Не указана"}</td>
              <td>{membership.is_active ? "Активен" : "Неактивен"}</td>
              <td>
                <button
                  className="button button--text"
                  type="button"
                  aria-label="Удалить участника"
                  disabled={pending}
                  onClick={() => onRemove(membership)}
                >
                  Удалить
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScopeDetail({
  services,
  scopeId,
  departmentId,
}: {
  services: ShellServices;
  scopeId: string;
  departmentId?: string;
}) {
  const client = useQueryClient();
  // Назначения ролей выполняются в активном контуре, поэтому страница контура
  // сначала делает его активным.
  useEffect(() => {
    void Promise.resolve(services.administration.selectScope(scopeId)).then(
      async () => {
        await client.invalidateQueries({ queryKey: shellBootstrapKey });
        await client.invalidateQueries({ queryKey: ["access", "roles"] });
      },
    );
  }, [client, scopeId, services.administration]);
  const [editing, setEditing] = useState<Department | null>(null);
  const [deletingDepartment, setDeletingDepartment] =
    useState<Department | null>(null);
  const [scopeSettings, setScopeSettings] = useState<AccessScope | null>(null);
  const [deletingScope, setDeletingScope] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [member, setMember] = useState({
    user_id: "",
    target_id: WHOLE_SCOPE,
    role_id: "",
  });
  const [userSearch, setUserSearch] = useState("");
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const deferredUserSearch = useDeferredValue(userSearch.trim());
  const scopes = useQuery({
    queryKey: ["scopes", "available"],
    queryFn: () => services.administration.listScopes(),
    retry: false,
  });
  const scope = scopes.data?.find((item) => item.id === scopeId);
  const scopeName = scope?.name ?? "Организация";
  // Головную организацию отличаем по типу из API, а не по её коду.
  const isCore = scope?.type === "core";
  const scopeWord = isCore ? "организацией" : "КПО";
  const scopeGenitive = isCore ? "организации" : "КПО";
  const departments = useQuery({
    queryKey: ["scope", scopeId, "departments"],
    queryFn: () => services.administration.listDepartments(scopeId),
    retry: false,
  });
  const memberships = useQuery({
    queryKey: ["scope", scopeId, "memberships"],
    queryFn: () => services.administration.listMemberships(scopeId),
    retry: false,
  });
  const roles = useQuery({
    queryKey: ["access", "roles", scopeId],
    queryFn: () => services.administration.listRoles(),
    retry: false,
  });
  const openedDepartment = departmentId
    ? departments.data?.find((department) => department.id === departmentId)
    : undefined;
  const displayName = openedDepartment?.name ?? scopeName;
  // Цели назначения всегда внутри текущего контура. Открытое подразделение
  // фиксирует цель — выбирать нечего.
  const assignmentTargets: AssignmentTarget[] = departmentId
    ? [{ id: departmentId, name: displayName, departmentId }]
    : [
        {
          id: WHOLE_SCOPE,
          name: `Весь ${scopeGenitive} · ${scopeName}`,
          departmentId: null,
        },
        ...(departments.data ?? []).map((department) => ({
          id: department.id,
          name: department.name,
          departmentId: department.id,
        })),
      ];
  const selectedTargetId = departmentId ?? member.target_id;
  const selectedTarget =
    assignmentTargets.find((target) => target.id === selectedTargetId) ??
    assignmentTargets[0];
  const membersDirectory = useQuery({
    queryKey: [
      "scope",
      scopeId,
      "user-search",
      selectedTarget?.departmentId ?? WHOLE_SCOPE,
      deferredUserSearch,
    ],
    queryFn: () =>
      services.administration.listUsers({
        ...(deferredUserSearch ? { search: deferredUserSearch } : {}),
        ...(selectedTarget?.departmentId
          ? { department: selectedTarget.departmentId }
          : { scope: scopeId }),
        per_page: 10,
      }),
    enabled: memberSearchOpen,
    retry: false,
  });
  // Системные роли из формы назначения исключены намеренно: их выдают в
  // карточке пользователя.
  const assignableRoles: Role[] = (roles.data ?? []).filter(
    (role) => !role.is_system,
  );
  const invalidate = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["scope", scopeId] }),
      client.invalidateQueries({ queryKey: ["scopes"] }),
      client.invalidateQueries({ queryKey: ["users"] }),
      client.invalidateQueries({ queryKey: ["user"] }),
      client.invalidateQueries({ queryKey: shellBootstrapKey }),
    ]);
  };
  const departmentMutation = useMutation({
    mutationFn: (input: {
      code: string;
      name: string;
      parent_id?: string | null;
    }) =>
      editing
        ? services.administration.updateDepartment(scopeId, editing.id, input)
        : services.administration.createDepartment(scopeId, input),
    onSuccess: async () => {
      setNotice({
        tone: "success",
        text: editing ? "Подразделение обновлено." : "Подразделение создано.",
      });
      setEditing(null);
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  const deleteDepartmentMutation = useMutation({
    mutationFn: (id: string) =>
      services.administration.deleteDepartment(scopeId, id),
    onSuccess: async () => {
      setDeletingDepartment(null);
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  const scopeMutation = useMutation({
    mutationFn: (value: AccessScope) =>
      services.administration.updateScope(value.id, {
        code: value.code,
        name: value.name,
        is_active: value.is_active,
      }),
    onSuccess: async () => {
      setScopeSettings(null);
      setNotice({ tone: "success", text: "Данные обновлены." });
      await client.invalidateQueries({ queryKey: ["scopes"] });
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  const deleteScopeMutation = useMutation({
    mutationFn: () => services.administration.deleteScope(scopeId),
    onSuccess: async () => {
      setDeletingScope(false);
      await client.invalidateQueries({ queryKey: ["scopes"] });
      window.location.assign("/administration/scopes");
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  const membershipMutation = useMutation({
    mutationFn: async () => {
      if (!member.role_id) {
        throw new Error("Выберите роль для участника.");
      }
      const targetDepartmentId = selectedTarget?.departmentId ?? null;
      await services.administration.saveMembership(scopeId, {
        user_id: member.user_id,
        department_id: targetDepartmentId,
      });
      // Организация названа явно: роль выдаётся в той, чей состав открыт,
      // а не в выбранной в шапке (ES-99).
      await services.administration.assignRole(
        member.role_id,
        member.user_id,
        targetDepartmentId,
        { scopeId },
      );
    },
    onSuccess: async () => {
      setMember({
        user_id: "",
        target_id: departmentId ?? WHOLE_SCOPE,
        role_id: "",
      });
      setUserSearch("");
      setNotice({
        tone: "success",
        text: `Данные участника в «${displayName}» обновлены.`,
      });
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  const removeMutation = useMutation({
    mutationFn: (membership: Membership) =>
      services.administration.deleteMembership(scopeId, membership.id),
    onSuccess: async () => {
      setNotice({ tone: "success", text: "Участник удалён из контекста." });
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: message(error) }),
  });
  if (departments.isPending || memberships.isPending)
    return <LoadingPage label="Загружаем данные контекста" />;
  if (departments.isError || memberships.isError)
    return (
      <EmptyState
        title="Контекст недоступен"
        description={message(departments.error ?? memberships.error)}
        action={{
          label: "К структуре компании",
          onClick: () => history.back(),
        }}
      />
    );
  const visibleDepartments = departments.data.filter((department) =>
    departmentId
      ? department.parent_id === departmentId
      : department.parent_id === null,
  );
  const visibleMemberships = departmentId
    ? memberships.data.filter((item) => item.department_id === departmentId)
    : memberships.data;
  return (
    <section className="workspace-page scope-page">
      <a
        className="back-link"
        href={
          departmentId
            ? `/administration/scopes/${scopeId}`
            : "/administration/scopes"
        }
      >
        ← {departmentId ? scopeName : "Структура компании"}
      </a>
      <div className="page-heading">
        <p className="eyebrow">
          Управление {departmentId ? "подразделением" : scopeWord}
        </p>
        <h1>{displayName}</h1>
        <p>
          UUID {departmentId ? "подразделения" : scopeGenitive}:{" "}
          <code>{departmentId ?? scopeId}</code>
        </p>
      </div>
      {notice ? (
        <p
          className={`operation-notice operation-notice--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}
      {!departmentId && scope ? (
        <section className="scope-section">
          <div className="panel-heading">
            <div>
              <h2>Настройки {scopeGenitive}</h2>
              <p>Код и название видны во всех списках и переключателе.</p>
            </div>
            {scopeSettings ? (
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setScopeSettings(null)}
              >
                Отмена
              </button>
            ) : (
              <div className="department-list__actions">
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => setScopeSettings(scope)}
                >
                  Редактировать
                </button>
                <button
                  className="button button--text"
                  type="button"
                  onClick={() => setDeletingScope(true)}
                >
                  Удалить {scopeGenitive}
                </button>
              </div>
            )}
          </div>
          {scopeSettings ? (
            <form
              className="scope-inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                scopeMutation.mutate(scopeSettings);
              }}
            >
              <label>
                <span>Код</span>
                <input
                  required
                  maxLength={64}
                  value={scopeSettings.code}
                  onChange={(event) =>
                    setScopeSettings({
                      ...scopeSettings,
                      code: event.target.value,
                    })
                  }
                />
              </label>
              <label>
                <span>Название</span>
                <input
                  required
                  maxLength={255}
                  value={scopeSettings.name}
                  onChange={(event) =>
                    setScopeSettings({
                      ...scopeSettings,
                      name: event.target.value,
                    })
                  }
                />
              </label>
              <button
                className="button button--primary"
                disabled={scopeMutation.isPending}
              >
                {scopeMutation.isPending ? "Сохраняем…" : "Сохранить"}
              </button>
            </form>
          ) : null}
        </section>
      ) : null}
      <section className="scope-section">
        <div className="panel-heading">
          <div>
            <h2>
              {editing ? "Редактировать подразделение" : "Новое подразделение"}
            </h2>
            <p>Подразделения создаются внутри {displayName}.</p>
          </div>
          {editing ? (
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setEditing(null)}
            >
              Отмена
            </button>
          ) : null}
        </div>
        <DepartmentForm
          key={editing?.id ?? "new"}
          department={editing}
          departments={departments.data}
          defaultParentId={departmentId}
          pending={departmentMutation.isPending}
          onSave={(input) => departmentMutation.mutate(input)}
        />
      </section>
      <section className="scope-section">
        <div className="panel-heading">
          <div>
            <h2>Подразделения</h2>
            <p>
              {subdivisionCount(visibleDepartments.length)} в {displayName}
            </p>
          </div>
        </div>
        <ul className="department-list" aria-label="Список подразделений">
          {visibleDepartments.map((department) => (
            <li key={department.id}>
              <span>
                <strong>{department.name}</strong>
                <small>Подразделение · {department.code}</small>
              </span>
              <div className="department-list__actions">
                <a
                  className="button button--secondary button--compact"
                  href={`/administration/scopes/${scopeId}/departments/${department.id}`}
                >
                  Открыть
                </a>
                <button
                  className="button button--secondary button--compact"
                  type="button"
                  onClick={() => setEditing(department)}
                >
                  Редактировать
                </button>
                <button
                  className="button button--text button--compact"
                  type="button"
                  onClick={() => setDeletingDepartment(department)}
                >
                  Удалить
                </button>
              </div>
            </li>
          ))}
        </ul>
        {!visibleDepartments.length ? (
          <p className="scope-empty">В {displayName} пока нет подразделений.</p>
        ) : null}
      </section>
      {deletingDepartment || deletingScope ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h2>
              {deletingScope
                ? `Удалить «${scopeName}»?`
                : `Удалить подразделение «${deletingDepartment?.name}»?`}
            </h2>
            <p>Действие необратимо.</p>
            <div className="modal__actions">
              <button
                className="button button--secondary"
                type="button"
                onClick={() => {
                  setDeletingDepartment(null);
                  setDeletingScope(false);
                }}
              >
                Отмена
              </button>
              <button
                className="button button--primary"
                type="button"
                onClick={() =>
                  deletingScope
                    ? deleteScopeMutation.mutate()
                    : deletingDepartment
                      ? deleteDepartmentMutation.mutate(deletingDepartment.id)
                      : undefined
                }
              >
                Да
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <section className="scope-section">
        <div className="panel-heading">
          <div>
            <h2>Назначить участника</h2>
            <p>
              Сотрудник добавляется в {displayName} и получает выбранную роль.
            </p>
          </div>
        </div>
        <form
          className="scope-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            membershipMutation.mutate();
          }}
        >
          <label>
            <span>Поиск сотрудника</span>
            <input
              required
              value={userSearch}
              placeholder="Имя или email"
              onFocus={() => setMemberSearchOpen(true)}
              onChange={(event) => {
                setUserSearch(event.target.value);
                setMemberSearchOpen(true);
              }}
            />
          </label>
          <label>
            <span>Подразделение</span>
            <Select
              value={selectedTargetId}
              disabled={Boolean(departmentId)}
              onChange={(event) =>
                setMember({ ...member, target_id: event.target.value })
              }
            >
              {assignmentTargets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.name}
                </option>
              ))}
            </Select>
          </label>
          <label>
            <span>Роль</span>
            <Select
              aria-label="Роль"
              value={member.role_id}
              onChange={(event) =>
                setMember({ ...member, role_id: event.target.value })
              }
              disabled={roles.isPending || roles.isError}
            >
              <option value="">
                {roles.isPending ? "Загружаем роли…" : "Выберите роль"}
              </option>
              {assignableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Select>
          </label>
          <button
            className="button button--primary"
            disabled={
              membershipMutation.isPending ||
              !member.user_id ||
              !member.role_id ||
              roles.isPending ||
              roles.isError
            }
          >
            {membershipMutation.isPending ? "Назначаем…" : "Назначить"}
          </button>
        </form>
        {roles.isError ? (
          <Notice>Не удалось загрузить роли. Повторите попытку.</Notice>
        ) : null}
        {membersDirectory.data?.data.length ? (
          <ul
            className="member-search-results"
            aria-label="Найденные сотрудники"
          >
            {membersDirectory.data.data.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  onClick={() => {
                    setMember({ ...member, user_id: user.id });
                    setUserSearch(user.display_name);
                    setMemberSearchOpen(false);
                  }}
                >
                  <strong>{user.display_name}</strong>
                  <small>{user.email}</small>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <section className="scope-section">
        <div className="panel-heading">
          <div>
            <h2>Состав {displayName}</h2>
            <p>Роль и подразделение отображаются в контексте {displayName}.</p>
          </div>
        </div>
        <MembershipTable
          memberships={visibleMemberships}
          departments={departments.data}
          contextName={displayName}
          pending={removeMutation.isPending}
          onRemove={(membership) => removeMutation.mutate(membership)}
        />
      </section>
    </section>
  );
}

export function ScopeManagementPage({
  services,
  scopeId,
  departmentId,
}: {
  services: ShellServices;
  scopeId?: string;
  departmentId?: string;
}) {
  return scopeId ? (
    <ScopeDetail
      services={services}
      scopeId={scopeId}
      departmentId={departmentId}
    />
  ) : (
    <section className="workspace-page scope-page">
      <div className="page-heading">
        <p className="eyebrow">Администрирование</p>
        <h1>Структура компании</h1>
        <p>Контуры, к которым вам выдан доступ.</p>
      </div>
      <ScopeList services={services} />
    </section>
  );
}
